import React, { memo, useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import type { Entry } from 'contentful';
import type {
  ColourField,
  DynamicContentSlotContentType,
  DLClickEventProps,
  DLRibbonPromotionProps,
  RichTextContentType,
  RichTextEntryType,
} from 'tsconfig/types';
import { globals } from '../../../utils/globals';
import { inspectorMode } from 'common-ui';
import { CmsRichText } from '../../contentful/CmsRichText/CmsRichText';
import type { CmsRichTextLinkClickDetails } from '../../contentful/CmsRichText/CmsRichText';
import { adobeDataLayer } from '../../../data-layer/AdobeDataLayer';
import {
  renderDynamicContentSlot,
  isRichTextType,
  isDynamicContentSlotType,
  hideStripBannerForPage,
} from '../stripBannerHelpers';

export type CmsStripBannerProps = {
  cmsStripBanner?: Entry<DynamicContentSlotContentType | RichTextContentType, 'WITHOUT_UNRESOLVABLE_LINKS', string>;
  cmsStripBannerBg?: ColourField;
  id?: string;
  pageSlug?: string;
  stripBannerDisabledPages?: string[];
};

const handleRibbonLinkClick = (
  details: CmsRichTextLinkClickDetails,
  contentItem: RichTextEntryType,
  sourceIndex: number,
): void => {
  const promotionId = contentItem.fields?.promotionId?.trim();
  const promotionName = contentItem.fields?.promotionName?.trim();

  if (!promotionId || !promotionName || !details.destinationUrl) {
    return;
  }

  const click: DLClickEventProps = {
    clickDescription: details.clickDescription,
    clickName: 'top_ribbon_click',
    clickLocation: 'top_ribbon',
    clickText: details.clickText,
    clickAction: 'navigate',
  };
  const promotion: DLRibbonPromotionProps = {
    promotionId,
    promotionName,
    creativeName: 'top_ribbon',
    creativeSlot: `top_ribbon_${sourceIndex + 1}`,
    destinationUrl: details.destinationUrl,
  };

  void adobeDataLayer.ribbonClickEvent(click, promotion);
};

export const StripBanner = memo((props: CmsStripBannerProps) => {
  const { cmsStripBannerBg, cmsStripBanner, pageSlug, stripBannerDisabledPages } = props;
  const contentTypeId = cmsStripBanner?.sys?.contentType?.sys?.id;
  const theme = useTheme();
  const widgetTheme = theme.widgets.GlobalHeader || {};
  const slickSliderWrapper = theme.widgets.SlickSlider?.wrapper;

  const wrapper = css`
    ${widgetTheme.stripBanner}
    ${cmsStripBannerBg && `background-color: ${cmsStripBannerBg.value};`}
  `;
  // Default to false so SSR/SSG renders the banner
  const [isDWAPPMobile, setIsDWAPPMobile] = useState<boolean>(false);

  useEffect(() => {
    // DSP-1478 - hide stripe banner for userAgent=DWAPP and mobile only
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;

      // Check if the user agent contains "DWAPP"
      const isDWAPP = userAgent.includes('DWAPP');

      // Basic mobile check
      const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone|webOS|Opera Mini|IEMobile/.test(
        userAgent,
      );

      // Set state based on the checks
      setIsDWAPPMobile(isDWAPP && isMobile);
    }
  }, []);

  // Early return if banner should be hidden or no content
  if (
    globals.isEmbedded ||
    isDWAPPMobile ||
    !contentTypeId ||
    hideStripBannerForPage(pageSlug, stripBannerDisabledPages)
  ) {
    return null;
  }

  // Compute the content before rendering the wrapper
  const content = (() => {
    if (isRichTextType(cmsStripBanner)) {
      return (
        <CmsRichText
          contentItem={cmsStripBanner}
          onLinkClick={details => handleRibbonLinkClick(details, cmsStripBanner, 0)}
        />
      );
    }
    if (isDynamicContentSlotType(cmsStripBanner)) {
      return renderDynamicContentSlot(cmsStripBanner, slickSliderWrapper, handleRibbonLinkClick);
    }
    return null;
  })();

  // Don't render empty wrapper for unsupported content types
  if (!content) {
    return null;
  }

  return (
    <div data-test="widget-StripBanner" css={wrapper} {...inspectorMode(props.id, 'stripBanner')}>
      {content}
    </div>
  );
});
